import { useId, useRef, useState } from 'react';
import { DocSection, DocCard, Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import { saveEngineSettings } from '../../lib/api/engineSettings';

/** The mutable settings values the tuning UI reads/writes. */
type EngineSettingsValues = {
  K_FACTOR: number;
  NEWBIE_MATCH_THRESHOLD: number;
  NEWBIE_GRACE_MATCHES: number;
  NEWBIE_INACTIVE_DAYS: number;
  NEWBIE_K_MULTIPLIER: number;
  MOV_MAX_BONUS: number;
};

/** One tunable slider field (keyed to an EngineSettings value). */
type TuningField = {
  key: keyof EngineSettingsValues;
  label: string;
  icon: string;
  min: number;
  max: number;
  step: number;
  desc: string;
  hint: Record<number, string>;
  decimal?: boolean;
};

export interface EngineTuningSectionProps {
  /**
   * May the viewer CHANGE these settings? Defaults to false (#243).
   *
   * Engine settings are platform-global — one change re-tunes ELO for every
   * club — so editing belongs to a platform superadmin in /sa, not to club
   * configuration. A club user cannot hold a platform role at all, so the
   * panel here is a read-only explanation of what the engine is currently
   * doing. Hiding the control is a courtesy to the reader; the server refuses
   * the write regardless (apps/api/src/app.ts).
   */
  canWrite?: boolean;
  /**
   * The monolith's `Padel24Engine` wiring object (preserved in the reference artifact
   * legacy/App.jsx). Only `.settings` (the EngineSettings singleton: defaults,
   * serialize(), apply()) is read/written here. Typed to the fields the UI touches so
   * the body stays byte-identical to the monolith.
   */
  Padel24Engine: {
    settings: EngineSettingsValues & {
      defaults: EngineSettingsValues;
      serialize: () => EngineSettingsValues;
      apply: (saved: EngineSettingsValues) => void;
    };
  };
}

/**
 * Engine docs — "Tuning av engine" panel: live sliders that read/write the
 * EngineSettings singleton (lock → edit → save to Storage). Extracted verbatim
 * from the monolith (legacy/App.jsx, `TuningSection`).
 *
 * Deviations from the monolith body, forced by typed (no @ts-nocheck) extraction:
 *  - receives `Padel24Engine` as a prop instead of closing over the monolith
 *    singleton (that wiring object lives in the reference artifact legacy/App.jsx);
 *  - `useState`/`updateDraft` get minimal type annotations (the monolith relies
 *    on @ts-nocheck).
 *
 * Deliberate BEHAVIOUR changes since, which this header must keep naming — it
 * is the record of how far this component has drifted from the frozen
 * reference, and it is what the next person reads before touching it (#243):
 *  - `canWrite` (default false). These settings are platform-global, so
 *    editing belongs to a superadmin in /sa; the club portal shows and
 *    explains them without offering a control the server would refuse.
 *  - saving opens an in-app confirmation listing the exact values that change
 *    and the reach of the change, styled as destructive.
 *  - the monolith's verbatim `window.confirm(...)` on reset is GONE, replaced
 *    by that same modal. docs/architecture/DOMAIN_RULES.md forbids window.confirm, and one
 *    panel mixing a native dialog with an in-app one is worse than either.
 *  - the engine adopts a saved value only AFTER the write lands, and a failed
 *    write is surfaced instead of logged.
 *
 * Renamed to EngineTuningSection to keep the help barrel collision-free.
 */
export function EngineTuningSection({ Padel24Engine, canWrite = false }: EngineTuningSectionProps) {
  // Local state synced with Padel24Engine.settings. Uses Date.now()
  // as a tick to re-render when settings change via buttons.
  // (Deviation: value binding prefixed `_tick` — it is only a re-render trigger,
  // never read; the `^_` prefix satisfies the src no-unused-vars rule.)
  const [_tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);

  // Edit mode: false by default. The user must actively press "🔒 Endre" to
  // unlock the sliders. This prevents accidental changes on hover or scroll.
  // The values are not saved to Storage until the user clicks "💾 Lagre".
  const [editMode, setEditMode] = useState(false);
  // Draft holds temporary changes while the user is in edit mode
  const [draft, setDraft] = useState<EngineSettingsValues | null>(null);
  // What the user is being asked to confirm, or null. Two flows land here:
  // saving (platform-wide, irreversible in effect) and resetting to defaults.
  const [pending, setPending] = useState<'save' | 'reset' | null>(null);

  const s = Padel24Engine.settings;
  const d = s.defaults;
  const uid = safeId('engine-tuning', useId());
  // Focus starts on «Avbryt» in the confirmation: the other button re-tunes
  // every rating on the platform.
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // The values shown and used for comparison — from draft if in edit mode,
  // otherwise from what is actually active.
  const liveValues = editMode && draft ? draft : s.serialize();

  // Start edit mode: copy the current values into draft
  const startEditing = () => {
    setDraft(s.serialize());
    setEditMode(true);
  };

  // Cancel: discard draft, go back
  const cancelEditing = () => {
    setDraft(null);
    setEditMode(false);
  };

  // Save: apply the draft to EngineSettings + persist via the API (falls back
  // to Storage on non-http backends — see lib/api/engineSettings.ts).
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Runs only after the confirmation modal is accepted.
  //
  // The failure is SURFACED rather than logged. It used to console.error and
  // then clear the draft and leave edit mode regardless — so a save that never
  // reached the server looked exactly like one that did. Asking someone to
  // confirm a platform-wide change and then not telling them it failed is
  // worse than not asking.
  const confirmSave = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveEngineSettings(draft);
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre engine-innstillinger:', e);
      setSaveError(e instanceof Error ? e.message : 'Ukjent feil');
      setSaving(false);
      return; // the draft is kept, so the user can retry without re-entering it
    }
    // Only once the write has landed does the running engine adopt it.
    s.apply(draft);
    setSaving(false);
    setPending(null);
    setDraft(null);
    setEditMode(false);
    refresh();
  };

  // Change in draft (only while in edit mode)
  const updateDraft = (key: keyof EngineSettingsValues, value: number) => {
    setDraft((prev) => ({ ...(prev as EngineSettingsValues), [key]: value }));
  };

  // DEVIATION FROM THE VERBATIM MONOLITH BODY, deliberately: this was a
  // `window.confirm(...)`, which docs/architecture/DOMAIN_RULES.md forbids. It is converted
  // rather than left because the save flow beside it now opens a real modal,
  // and one panel offering a native dialog for one action and an in-app one
  // for the other is worse than either.
  const resetAll = () => setPending('reset');
  const confirmReset = () => {
    setDraft({ ...d });
    setPending(null);
  };

  /**
   * Exactly which values change, and from what to what.
   *
   * The confirmation lists these rather than asking "are you sure?". A dialog
   * that names no consequence is a speed bump: it trains people to accept it
   * without reading. One that shows `K_FACTOR 32 → 48` is something a person
   * can actually check before re-tuning every club's ratings.
   */
  const pendingChanges = draft
    ? (Object.keys(draft) as (keyof EngineSettingsValues)[])
        .filter((k) => draft[k] !== s.serialize()[k])
        .map((k) => ({ key: k, from: s.serialize()[k], to: draft[k] }))
    : [];

  // Check whether draft differs from the saved state
  const hasUnsavedChanges =
    editMode && draft && JSON.stringify(draft) !== JSON.stringify(s.serialize());
  // Check whether any value differs from defaults
  const hasNonDefault = JSON.stringify(liveValues) !== JSON.stringify(d);

  // Definitions: all adjustable fields with descriptions
  const fields: TuningField[] = [
    {
      key: 'K_FACTOR',
      label: 'K-faktor',
      icon: '⚡',
      min: 8,
      max: 64,
      step: 2,
      desc: 'Hovedmultiplikator for hvor mye ELO endres per kamp. Høyere = mer volatil rating, lavere = mer stabil. Endring her påvirker ALLE turneringer.',
      hint: { 16: 'svært stabil', 32: 'anbefalt', 48: 'volatil', 64: 'gammel standard' },
    },
    {
      key: 'NEWBIE_MATCH_THRESHOLD',
      label: 'Ny spiller-terskel (kamper)',
      icon: '🔰',
      min: 0,
      max: 30,
      step: 1,
      desc: 'Spillere med færre fullførte kamper enn dette regnes som nye spillere og får boost-multiplikator. Sett til 0 for å skru av ny-spiller-systemet.',
      hint: { 0: 'av', 10: 'rask etablering', 20: 'anbefalt', 30: 'langvarig boost' },
    },
    {
      key: 'NEWBIE_GRACE_MATCHES',
      label: 'Full-boost-periode (kamper)',
      icon: '⏳',
      min: 0,
      max: 20,
      step: 1,
      desc: 'Antall kamper helt nye spillere får 100% boost FØR den lineære avtaket starter. Med terskel=20 og grace=5: full boost kamp 1-5, deretter lineær avtak mot 1x ved kamp 20.',
      hint: { 0: 'ren lineær', 3: 'kort', 5: 'anbefalt', 10: 'rause første kamper' },
    },
    {
      key: 'NEWBIE_K_MULTIPLIER',
      label: 'Ny spiller-boost (multiplikator)',
      icon: '✨',
      min: 1.0,
      max: 3.0,
      step: 0.25,
      decimal: true,
      desc: 'Hvor mye ekstra ELO-utslag nye spillere får under full-boost-perioden. Avtar lineært mot 1x når terskelen er nådd.',
      hint: { 1.0: 'av', 1.5: 'mild', 2.0: 'anbefalt', 3.0: 'aggressiv' },
    },
    {
      key: 'NEWBIE_INACTIVE_DAYS',
      label: 'Tilbakekomst-terskel (dager)',
      icon: '🔄',
      min: 30,
      max: 365,
      step: 30,
      desc: 'Spillere som ikke har spilt på så mange dager regnes som "tilbake fra pause" og får samme boost som nye spillere. Settes høyt for å unngå at sommerferie utløser boost.',
      hint: { 30: '1 mnd', 90: '3 mnd', 180: 'anbefalt (6 mnd)', 365: '1 år' },
    },
    {
      key: 'MOV_MAX_BONUS',
      label: 'MOV-maks-bonus (margin)',
      icon: '🎯',
      min: 0.0,
      max: 1.0,
      step: 0.1,
      decimal: true,
      desc: 'Hvor mye ekstra ELO knusende seire gir. 0.5 = inntil 50% bonus (12-0 gir 1.5x effekt). 0 = ingen MOV-bonus.',
      hint: { 0: 'av', 0.3: 'mild', 0.5: 'anbefalt', 1.0: 'aggressiv' },
    },
  ];

  // ── the confirmation, for both flows ───────────────────────────────
  //
  // A `Dialog` (#900). The role and `aria-modal` used to sit on the BACKDROP —
  // the surface that dismisses it — and it had no name. Now the panel is the
  // dialog, named by its question. Escape is «Avbryt», and like the backdrop
  // it does nothing while a save is in flight.
  const cancelPending = () => {
    if (saving) return;
    setPending(null);
    setSaveError(null);
  };
  const confirmModal = pending && (
    <Dialog
      labelledBy={`${uid}-title`}
      onClose={cancelPending}
      showClose={false}
      initialFocus={cancelRef}
      backdropClassName="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      className="bg-bg-soft rounded-2xl shadow-xl w-full max-w-md my-6 p-5"
    >
      {/* The test id the backdrop used to carry, on a wrapper with no box of
          its own, so what it names (the whole confirmation) is unchanged. */}
      <div data-testid="engine-tuning-confirm">
        <h3
          id={`${uid}-title`}
          className="font-display text-sm font-semibold"
          style={pending === 'save' ? { color: '#9f1239' } : undefined}
        >
          {pending === 'save'
            ? '⚠️ Endre ELO for hele plattformen?'
            : 'Tilbakestille til standard?'}
        </h3>

        {pending === 'save' ? (
          <>
            {/* DANGER STYLING, on this flow only.
                Saving re-tunes every rating on the platform, and it cannot be
                undone by changing the number back: old results are not
                recalculated, so whatever was computed in between keeps the
                rating it got. The reset flow beside it stages defaults into the
                DRAFT and writes nothing, so it stays neutral — reddening both
                would spend the warning on the harmless one. */}
            <div
              className="mt-2 rounded-xl p-3"
              style={{ background: '#fff1f2', border: '1px solid #fda4af' }}
              data-testid="engine-tuning-confirm-warning"
            >
              <p className="text-xs font-semibold" style={{ color: '#9f1239' }}>
                Dette er en plattform-innstilling og gjelder alle klubber.
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#9f1239' }}>
                Endringen trer i kraft for alle nye kamper. Gamle resultater rekalkuleres ikke, så
                rating som allerede er beregnet blir stående.
              </p>
            </div>
            <ul className="mt-3 space-y-1" data-testid="engine-tuning-confirm-changes">
              {pendingChanges.map((c) => (
                <li key={String(c.key)} className="text-xs font-mono">
                  {String(c.key)}: <span className="text-ink-3">{String(c.from)}</span> →{' '}
                  <strong>{String(c.to)}</strong>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-[11px] text-ink-3 mt-1">
            Setter alle verdier tilbake til standard. Du må fortsatt trykke «Lagre» for å bekrefte.
          </p>
        )}

        {saveError && (
          <p className="text-[11px] text-red-400 mt-3" data-testid="engine-tuning-save-error">
            Kunne ikke lagre ({saveError}). Ingenting er endret — prøv igjen.
          </p>
        )}

        <div className="flex items-center gap-2 justify-end mt-4">
          <button
            ref={cancelRef}
            type="button"
            className="btn-ghost text-xs"
            disabled={saving}
            onClick={() => {
              setPending(null);
              setSaveError(null);
            }}
          >
            Avbryt
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-full text-xs font-mono font-bold disabled:opacity-40"
            /* RED AND FILLED for the save: the same treatment the app gives
               its other destructive actions, rather than the green it shares
               with ordinary saves. The reset keeps the neutral action colour. */
            style={
              pending === 'save'
                ? { background: '#dc2626', color: '#fff', border: '1px solid #b91c1c' }
                : { background: '#c2f02f', color: '#0f1611', border: '1px solid #a3e635' }
            }
            disabled={saving}
            data-testid="engine-tuning-confirm-accept"
            onClick={() => void (pending === 'save' ? confirmSave() : confirmReset())}
          >
            {saving
              ? 'Lagrer…'
              : pending === 'save'
                ? 'Ja, endre for alle klubber'
                : 'Tilbakestill'}
          </button>
        </div>
      </div>
    </Dialog>
  );

  return (
    <DocSection title="Tuning av engine">
      {confirmModal}
      <p>
        Disse innstillingene styrer hvordan ELO-systemet oppfører seg. Endringer trer i kraft
        umiddelbart for alle nye kamper — gamle resultater rekalkuleres ikke. Innstillingene lagres
        lokalt på din enhet.
      </p>

      <DocCard className="bg-bg-soft" style={{ marginTop: 16 }}>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Nåværende innstillinger
            {editMode && hasUnsavedChanges && (
              <span className="ml-2 text-orange normal-case font-normal">(ulagrede endringer)</span>
            )}
          </div>

          {/* Button row — changes based on mode. Absent entirely without
              write access: an "Endre" the server would refuse is worse than no
              button, because it reads as an offer. */}
          {!canWrite ? (
            <span className="text-[11px] text-ink-3" data-testid="engine-tuning-readonly">
              Endres av Starpoint (plattform-innstilling)
            </span>
          ) : !editMode ? (
            <button
              onClick={startEditing}
              className="px-3 py-1.5 rounded-full text-xs font-mono font-bold transition-colors"
              style={{ background: 'white', color: '#3a4a40', border: '1px solid #d4dad6' }}
              title="Lås opp slider'ene for å justere"
            >
              🔒 Endre
            </button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {hasNonDefault && (
                <button
                  onClick={resetAll}
                  className="text-[11px] font-mono text-rose hover:underline"
                >
                  ← Tilbakestill alle
                </button>
              )}
              <button
                onClick={cancelEditing}
                className="px-3 py-1.5 rounded-full text-xs font-mono font-bold transition-colors"
                style={{ background: 'white', color: '#3a4a40', border: '1px solid #d4dad6' }}
              >
                Avbryt
              </button>
              <button
                onClick={() => setPending('save')}
                disabled={!hasUnsavedChanges}
                className="px-3 py-1.5 rounded-full text-xs font-mono font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: hasUnsavedChanges ? '#c2f02f' : '#e7eae3',
                  color: '#0f1611',
                  border: '1px solid #a3e635',
                }}
              >
                💾 Lagre
              </button>
            </div>
          )}
        </div>

        <div
          className="space-y-4 transition-opacity"
          style={{ opacity: editMode ? 1 : 0.5, pointerEvents: editMode ? 'auto' : 'none' }}
        >
          {fields.map((f) => {
            const current = liveValues[f.key];
            const isDefault = current === d[f.key];
            const displayValue = f.decimal ? current.toFixed(2) : current;

            return (
              <div key={f.key} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label
                    htmlFor={`${uid}-${f.key}`}
                    className="font-mono text-xs font-semibold text-ink flex items-center gap-1.5"
                  >
                    <span>{f.icon}</span> {f.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <span
                      className="font-mono text-sm font-bold px-2.5 py-0.5 rounded-full"
                      style={{
                        background: isDefault ? '#f3f4f6' : '#fef3c7',
                        color: isDefault ? '#374151' : '#92400e',
                      }}
                    >
                      {displayValue}
                    </span>
                    {!isDefault && editMode && (
                      <button
                        onClick={() => updateDraft(f.key, d[f.key])}
                        className="text-[10px] font-mono text-ink-3 hover:text-ink-1 underline-offset-2 hover:underline"
                        title={`Tilbakestill til standardverdi (${f.decimal ? d[f.key].toFixed(2) : d[f.key]})`}
                      >
                        reset
                      </button>
                    )}
                  </div>
                </div>

                <p className="text-[11px] text-ink-2 mb-2 leading-relaxed">{f.desc}</p>

                <input
                  id={`${uid}-${f.key}`}
                  type="range"
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={current}
                  disabled={!editMode}
                  onChange={(e) => {
                    const v = f.decimal ? parseFloat(e.target.value) : parseInt(e.target.value, 10);
                    updateDraft(f.key, v);
                  }}
                  className="w-full cursor-pointer disabled:cursor-not-allowed"
                  style={{ accentColor: '#65a30d' }}
                />

                <div className="flex justify-between text-[9px] font-mono text-ink-3 mt-1 px-0.5">
                  {/* REAL BUTTONS (#900), so a keyboard reaches the same
                      shortcuts a pointer does. `text-left` and the inherited
                      font keep the old span's look; disabled outside edit mode,
                      where the span did nothing either. */}
                  {Object.entries(f.hint).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      disabled={!editMode}
                      aria-pressed={current === parseFloat(val)}
                      className={`text-left ${editMode ? 'cursor-pointer hover:text-ink-1' : ''}`}
                      onClick={() => updateDraft(f.key, parseFloat(val))}
                      style={{
                        fontWeight: current === parseFloat(val) ? 700 : 400,
                        color: parseFloat(val) === d[f.key] ? '#65a30d' : undefined,
                      }}
                      title={editMode ? `Klikk for å sette til ${val}` : undefined}
                    >
                      {f.decimal ? parseFloat(val).toFixed(2) : val}
                      <br />
                      <span style={{ color: '#94a098' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </DocCard>

      <DocCard className="bg-rose-soft" style={{ marginTop: 16 }}>
        <div className="font-semibold text-sm mb-2" style={{ color: '#9f1239' }}>
          ⚠ Husk om endringer
        </div>
        <ul className="list-disc list-inside text-sm text-ink-2 space-y-1.5">
          <li>
            Endringer påvirker kun <strong>nye kamper</strong> — eksisterende ELO blir stående
          </li>
          <li>Innstillingene lagres lokalt og synkroniseres ikke mellom enheter</li>
          <li>Reset-knappene over setter tilbake til engine-standard</li>
          <li>For "ikke gjør noe"-effekt: sett K-faktor til 0 eller MOV-bonus til 0</li>
        </ul>
      </DocCard>

      <h3 className="font-display text-lg font-semibold mt-6 mb-2">
        Andre tunbare ting (krever kode)
      </h3>
      <p className="text-sm">Disse er ikke justerbare i UI ennå:</p>
      <ul className="list-disc list-inside text-sm text-ink-2 mt-2 space-y-1">
        <li>
          <span className="font-mono">ELO_LEVELS</span> — terskler for
          Nybegynner/Mosjonist/Konkurranse/Elite-nivåene
        </li>
        <li>
          <span className="font-mono">drawing.weights</span> — vekter i trekningsalgoritmen
          (PARTNER_PENALTY etc.)
        </li>
        <li>
          <span className="font-mono">PACING_MODES</span> — tidsfordeling for R1/R2 innen samme fase
        </li>
        <li>
          <span className="font-mono">kMultiplier per format</span> — settes per turnering, ikke
          globalt
        </li>
      </ul>
    </DocSection>
  );
}
